/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Building2, FileText, Trash2, Upload, Send, Users, Briefcase, Save, FilePlus, Check, X, Edit3, Sparkles, ChevronDown, ChevronUp, Download, MessageSquare, Plus } from 'lucide-react';
import { api } from '../api';
import { Badge } from '../components/ui';
import { AgentAvatar } from '../components/photo';
import { withDayDividers } from '../components/dateUtils';
import { RichText } from '../components/richText';

const DayDivider = ({ label }) => (
  <div className="flex items-center gap-3 my-2 select-none" data-testid="day-divider">
    <div className="flex-1 h-px bg-white/[0.05]" />
    <span className="text-[10px] uppercase tracking-[0.22em] text-amber-200/50 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-0.5">{label}</span>
    <div className="flex-1 h-px bg-white/[0.05]" />
  </div>
);

const DOC_TYPES = ['Draft', 'Reference', 'Contract', 'Filing', 'KYC', 'Correspondence', 'Other'];
const GEN_DOC_TYPES = ['NDA', 'Contract', 'Client Brief', 'Filing Letter', 'Legal Opinion', 'Response Draft', 'Other'];
const DOC_STATUS_TONES = { 'Draft': 'amber', 'Changes Required': 'rose', 'Approved': 'emerald', 'Rejected': 'slate' };

// ---------- Discussion Log (next to Context) ----------
function DiscussionLog({ matterId, entries, onChanged }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true); setErr('');
    try {
      await api.post(`/matters/${matterId}/discussion-log`, { text: draft.trim() });
      setDraft('');
      onChanged?.();
    } catch (x) { setErr(x?.response?.data?.detail || 'Failed to log'); }
    finally { setBusy(false); }
  };
  const del = async (eid) => {
    if (!confirm('Delete this log entry?')) return;
    try { await api.delete(`/matters/${matterId}/discussion-log/${eid}`); onChanged?.(); }
    catch (x) { alert(x?.response?.data?.detail || 'Delete failed'); }
  };
  const sorted = [...entries].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  return (
    <div className="flex flex-col" data-testid="m-discussion-log">
      <div className="flex items-center justify-between mb-1">
        <Label>Discussion log</Label>
        <span className="text-[10px] text-slate-500">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
      </div>
      <div className="flex-1 rounded-lg bg-black/20 border border-white/[0.06] p-2 space-y-2 min-h-[160px] max-h-[300px] overflow-y-auto mb-2">
        {sorted.length === 0 ? (
          <div className="text-slate-600 text-[11.5px] italic text-center py-6">
            No entries yet. Log a call summary, a client update, a team decision — anything the agents shouldn&apos;t reply to but the team must remember.
          </div>
        ) : sorted.map((e) => (
          <div key={e.id} className="rounded-md bg-white/[0.03] border border-white/[0.04] px-2.5 py-1.5 group" data-testid={`log-${e.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] text-amber-200/70">
                <MessageSquare size={9} className="inline mr-1" />
                {e.authorName} <span className="text-slate-600">· {new Date(e.at).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <button onClick={() => del(e.id)} className="text-slate-500 hover:text-rose-300 opacity-0 group-hover:opacity-100" title="Delete"><Trash2 size={10} /></button>
            </div>
            <div className="text-slate-100 text-[12px] mt-0.5 whitespace-pre-wrap">{e.text}</div>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="flex gap-1.5">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Log a discussion, call note, or team decision…"
          className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-[12.5px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40"
          data-testid="log-input" />
        <button type="submit" disabled={busy || !draft.trim()}
          className="inline-flex items-center gap-1 text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-lg px-3 py-2 font-semibold disabled:opacity-40"
          data-testid="log-submit"><Plus size={11} strokeWidth={2.5} />{busy ? '…' : 'Log'}</button>
      </form>
      {err && <div className="text-rose-300 text-[10.5px] mt-1">{err}</div>}
    </div>
  );
}

// ---------- Generate document modal ----------
function GenerateDocModal({ open, matterId, onClose, onDone }) {
  const [docType, setDocType] = useState('NDA');
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/matters/${matterId}/documents/generate`, { docType, title: title || docType, instruction });
      onDone(r.data);
      onClose(); setTitle(''); setInstruction('');
    } catch (x) { setErr(x?.response?.data?.detail || 'Draft failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/[0.06] bg-slate-950 backdrop-blur-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="gen-doc-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-300" />
            <div>
              <div className="text-slate-50 font-medium text-[15px]">Ask Preparer to draft…</div>
              <div className="text-slate-500 text-[11px]">Kavya Iyer will draft this using the full matter + company context.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Type</div>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-slate-100 outline-none focus:border-amber-500/40">
                {GEN_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Title</div>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={`e.g. Mutual ${docType}`}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Instruction (optional)</div>
            <textarea rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)}
              placeholder="Specific clauses, tone, jurisdiction quirks…"
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none" />
          </div>
          {err && <div className="text-rose-300 text-[12px] bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-200 text-[12px] px-3">Cancel</button>
            <button type="submit" disabled={busy}
              className="inline-flex items-center gap-1.5 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[13px] px-5 py-2 rounded-full disabled:opacity-40 hover:brightness-110">
              {busy ? 'Drafting… (may take 10s)' : 'Draft it'}<Sparkles size={12} strokeWidth={2.5} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Review actions ----------
function ReviewButtons({ doc, onReview, busy }) {
  const [showComment, setShowComment] = useState(null); // 'approve' | 'reject' | 'changes'
  const [comment, setComment] = useState('');
  if (doc.status !== 'Draft' && doc.status !== 'Changes Required') return null;
  const decide = async () => {
    await onReview(doc.id, showComment, comment);
    setShowComment(null); setComment('');
  };
  return (
    <div className="mt-3 border-t border-white/[0.05] pt-3">
      {!showComment ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowComment('approve')} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] text-emerald-950 bg-emerald-400 hover:bg-emerald-300 rounded-full px-3 py-1.5 font-semibold" data-testid={`doc-approve-${doc.id}`}>
            <Check size={11} strokeWidth={3} /> Approve
          </button>
          <button onClick={() => setShowComment('changes')} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1.5 font-semibold" data-testid={`doc-changes-${doc.id}`}>
            <Edit3 size={11} strokeWidth={2.5} /> Changes required
          </button>
          <button onClick={() => setShowComment('reject')} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] text-rose-100 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 rounded-full px-3 py-1.5 font-semibold" data-testid={`doc-reject-${doc.id}`}>
            <X size={11} strokeWidth={2.5} /> Reject
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder={showComment === 'approve' ? 'Approval note (optional)' : showComment === 'changes' ? 'What needs to change?' : 'Reason (optional)'}
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none"
            autoFocus />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowComment(null)} className="text-[11px] text-slate-500 hover:text-slate-200">Cancel</button>
            <button onClick={decide} disabled={busy}
              className={`inline-flex items-center gap-1 text-[11px] rounded-full px-4 py-1.5 font-semibold ${
                showComment === 'approve' ? 'text-emerald-950 bg-emerald-400 hover:bg-emerald-300' :
                showComment === 'changes' ? 'text-amber-950 bg-amber-400 hover:bg-amber-300' :
                'text-rose-100 bg-rose-500/30 hover:bg-rose-500/40 border border-rose-500/40'
              }`}>
              {busy ? 'Sending…' : `Confirm ${showComment}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Generated doc card ----------
function GeneratedDocCard({ matterId, doc, onReview, busy }) {
  const [open, setOpen] = useState(false);
  const exportDoc = async (fmt) => {
    try {
      const token = localStorage.getItem('liq_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/liq/matters/${matterId}/documents/${doc.id}/export?format=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.name || 'document'}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(e.message || 'Export failed'); }
  };
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4" data-testid={`gen-doc-${doc.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={DOC_STATUS_TONES[doc.status] || 'slate'}>{doc.status}</Badge>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{doc.docType}</span>
          </div>
          <div className="text-slate-100 text-[14px] font-medium mt-1">{doc.name}</div>
          <div className="text-slate-500 text-[11px] mt-0.5">Drafted by {doc.generatedByName} · {doc.generatedAt ? new Date(doc.generatedAt).toLocaleString() : ''}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => exportDoc('pdf')}
            className="inline-flex items-center gap-1 text-[10.5px] text-amber-100 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 rounded-full px-2.5 py-1 font-semibold"
            title="Download PDF" data-testid={`doc-pdf-${doc.id}`}>
            <Download size={11} strokeWidth={2.5} /> PDF
          </button>
          <button onClick={() => exportDoc('docx')}
            className="inline-flex items-center gap-1 text-[10.5px] text-sky-100 bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/40 rounded-full px-2.5 py-1 font-semibold"
            title="Download DOCX" data-testid={`doc-docx-${doc.id}`}>
            <Download size={11} strokeWidth={2.5} /> DOCX
          </button>
          <button onClick={() => setOpen((s) => !s)} className="text-[11px] text-slate-400 hover:text-amber-100 border border-white/[0.08] hover:border-amber-500/30 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
            {open ? <>Hide <ChevronUp size={11} /></> : <>Read <ChevronDown size={11} /></>}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-2 max-h-[380px] overflow-y-auto rounded-lg bg-black/30 border border-white/[0.05] p-4">
          <pre className="text-slate-200 text-[12.5px] whitespace-pre-wrap font-sans leading-relaxed">{doc.content}</pre>
        </div>
      )}
      {doc.reviewComment && (
        <div className="mt-2 text-[11px] text-slate-400 bg-white/[0.02] border-l-2 border-amber-500/40 px-3 py-1.5 rounded">
          <span className="text-amber-200/80 font-medium">Note:</span> {doc.reviewComment}
        </div>
      )}
      <ReviewButtons doc={doc} onReview={onReview} busy={busy} />
    </div>
  );
}

// ---------- Uploaded doc row ----------
function DocRow({ doc, onOpen, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-amber-500/20 group">
      <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
        <FileText size={13} className="text-sky-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-slate-100 text-[12.5px] truncate">{doc.name}</div>
        <div className="text-slate-500 text-[10px]">{doc.docType || 'Other'} · {Math.round((doc.sizeBytes || 0) / 1024)} KB</div>
      </div>
      <button onClick={() => onOpen(doc)} className="text-[10px] text-amber-200/70 hover:text-amber-100 border border-white/[0.08] hover:border-amber-500/30 rounded-full px-2.5 py-1">Open</button>
      <button onClick={() => onDelete(doc)} className="text-slate-500 hover:text-rose-300 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
    </div>
  );
}

function DocUploader({ matterId, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState('Reference');
  const upload = async (file) => {
    setBusy(true);
    try {
      const init = await api.post(`/matters/${matterId}/documents/init-upload`, {
        filename: file.name, contentType: file.type, sizeBytes: file.size, docType,
      });
      const put = await fetch(init.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`S3 upload rejected (${put.status})`);
      await new Promise((r) => setTimeout(r, 600));
      const fin = await api.post(`/matters/${matterId}/documents/finalize`, {
        s3Key: init.data.s3Key, mediaId: init.data.mediaId, filename: file.name, contentType: file.type, docType,
      });
      onUploaded(fin.data);
    } catch (x) { alert(x?.response?.data?.detail || x.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={docType} onChange={(e) => setDocType(e.target.value)}
              className="bg-white/[0.03] border border-white/[0.08] rounded-full px-3 py-1.5 text-[11px] text-slate-200 outline-none focus:border-amber-500/40">
        {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label className="inline-flex items-center gap-1.5 text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1.5 cursor-pointer font-semibold">
        <Upload size={12} strokeWidth={2.5} /> {busy ? 'Uploading…' : 'Add doc'}
        <input type="file" className="hidden" disabled={busy}
               accept="application/pdf,image/jpeg,image/png,image/webp,.docx,.xlsx,.doc,.xls,.txt,.csv"
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </label>
    </div>
  );
}

// ---------- Group chat panel ----------
function GroupChat({ matterId, matter, agentsById }) {
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/matters/${matterId}/group-chat`).then((r) => {
    setMessages(r.data.messages);
    setParticipants(r.data.participants);
  });
  useEffect(() => { load(); }, [matterId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    const draft = text;
    setText('');
    // Optimistic
    setMessages((m) => [...m, { id: `t-${Date.now()}`, senderKind: 'human', senderName: 'You', text: draft, at: new Date().toISOString() }]);
    setMessages((m) => [...m, { id: 'typing', senderKind: 'agent', typing: true, text: '…', at: new Date().toISOString() }]);
    try {
      const r = await api.post(`/matters/${matterId}/group-chat/message`, { text: draft });
      setMessages((m) => m.filter((x) => x.id !== 'typing' && !x.id.startsWith('t-'))
        .concat([r.data.userMessage, ...(r.data.agentReplies || [])]));
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== 'typing'));
      alert(err?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden flex flex-col h-[600px]"
         data-testid="group-chat">
      <div className="px-5 py-3 border-b border-white/[0.06] bg-gradient-to-b from-white/[0.03] to-transparent flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
          <Users size={16} className="text-emerald-300" />
        </div>
        <div className="flex-1">
          <div className="text-slate-50 text-[14px] font-medium">Matter group chat</div>
          <div className="text-slate-400 text-[11px]">You + {participants.filter((p) => p.role !== 'Santosh').length} agents · they all reply on every message</div>
        </div>
        <div className="flex -space-x-2">
          {participants.filter((p) => p.role !== 'Santosh').map((p) => (
            <AgentAvatar key={p.id} agent={agentsById[p.id] || p} size={28} />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
           style={{ background: 'radial-gradient(600px 400px at 90% -10%, rgba(230,199,109,0.04), transparent), radial-gradient(500px 400px at -10% 100%, rgba(16,185,129,0.03), transparent)' }}
           data-testid="gc-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-8">
            Kick off the discussion. All 3 agents will respond with their view.
          </div>
        )}
        {withDayDividers(messages).map((entry) => {
          if (entry.type === 'day') return <DayDivider key={entry.key} label={entry.label} />;
          const m = entry.msg;
          const isHuman = m.senderKind === 'human';
          const agent = !isHuman && m.senderId ? agentsById[m.senderId] : null;
          return (
            <div key={m.id} className={`flex gap-2 ${isHuman ? 'justify-end' : 'justify-start'} animate-[fadeIn_0.25s_ease-out]`}>
              {!isHuman && (agent ? <AgentAvatar agent={agent} size={30} /> : <div className="w-[30px]" />)}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed shadow-lg ${
                isHuman
                  ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-emerald-50 rounded-br-sm'
                  : 'bg-white/[0.04] text-slate-100 border border-white/[0.06] rounded-bl-sm backdrop-blur'
              }`}>
                {!isHuman && (
                  <div className="text-[10px] font-medium text-amber-200/80 mb-0.5">
                    {m.senderName} <span className="text-slate-500">· {m.senderRole}</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap"><RichText text={m.text} /></div>
                <div className={`text-[10px] mt-1 ${isHuman ? 'text-emerald-100/70 text-right' : 'text-slate-500'}`}>
                  {m.typing ? (
                    <span className="inline-flex gap-1">
                      <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" />
                      <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </span>
                  ) : new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="border-t border-white/[0.06] px-4 py-3 flex gap-2 bg-white/[0.02] backdrop-blur">
        <input value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Ask all 3 agents…"
          className="flex-1 bg-white/[0.03] border border-white/[0.08] rounded-full px-4 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40"
          data-testid="gc-input" />
        <button type="submit" disabled={sending || !text.trim()}
          className="flex items-center gap-1.5 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[13px] px-5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:scale-[1.02] shadow-lg"
          data-testid="gc-send"><Send size={13} strokeWidth={2.5} />{sending ? '…' : 'Send'}</button>
      </form>
    </div>
  );
}

// ---------- Main ----------
export default function MatterDetail() {
  const { id } = useParams();
  const [matter, setMatter] = useState(null);
  const [client, setClient] = useState(null);
  const [agents, setAgents] = useState({}); // by id
  const [descDraft, setDescDraft] = useState('');
  const [ctxDraft, setCtxDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [docsFilter, setDocsFilter] = useState('All');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [chatBumpKey, setChatBumpKey] = useState(0);

  const load = () => api.get(`/matters/${id}`).then(async (r) => {
    setMatter(r.data);
    setDescDraft(r.data.description || '');
    setCtxDraft(r.data.context || '');
    // Client
    if (r.data.clientId) {
      const cr = await api.get(`/clients/${r.data.clientId}`);
      setClient(cr.data);
    }
    // Agents
    const er = await api.get('/employees');
    const map = {};
    er.data.items.forEach((e) => { map[e.id] = e; });
    setAgents(map);
  });
  useEffect(() => { load(); }, [id]);

  const saveText = async () => {
    setSaving(true);
    try {
      await api.patch(`/matters/${id}/quick-context`, { description: descDraft, context: ctxDraft });
      await load();
    } finally { setSaving(false); }
  };
  const openDoc = async (doc) => {
    const r = await api.get(`/matters/${id}/documents/${doc.id}/url`);
    if (r.data.signedUrl) window.open(r.data.signedUrl, '_blank', 'noopener');
  };
  const deleteDoc = async (doc) => {
    if (!confirm(`Delete "${doc.name}"?`)) return;
    await api.delete(`/matters/${id}/documents/${doc.id}`);
    load();
  };

  const reviewDoc = async (docId, decision, comment) => {
    setReviewBusy(true);
    try {
      await api.post(`/matters/${id}/documents/${docId}/review`, { decision, comment });
      await load();
      setChatBumpKey((k) => k + 1); // force group chat to reload messages
    } catch (x) { alert(x?.response?.data?.detail || 'Review failed'); }
    finally { setReviewBusy(false); }
  };

  if (!matter) return <div className="text-slate-500 text-sm">Loading matter…</div>;

  const teamAgents = [
    { role: 'Researcher', id: matter.researcherId },
    { role: 'Preparer', id: matter.preparerId },
    { role: 'Verifier', id: matter.verifierId },
  ].filter((x) => x.id);
  const dirty = descDraft !== (matter.description || '') || ctxDraft !== (matter.context || '');

  const allDocs = matter.documents || [];
  const generatedDocs = allDocs.filter((d) => d.source === 'generated');
  const uploadedDocs = allDocs.filter((d) => d.source !== 'generated');
  const filteredGen = docsFilter === 'All' ? generatedDocs : generatedDocs.filter((d) => d.status === docsFilter);
  const statusCounts = { All: generatedDocs.length };
  ['Draft', 'Changes Required', 'Approved', 'Rejected'].forEach((s) => { statusCounts[s] = generatedDocs.filter((d) => d.status === s).length; });

  return (
    <div data-testid="liq-matter-detail">
      <div className="mb-4">
        <Link to="/liq/matters" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-200">
          <ArrowLeft size={13} /> Matters
        </Link>
      </div>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
          <Briefcase size={22} className="text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] text-amber-200/80 tracking-wider" data-testid="m-code">{matter.matterCode}</div>
          <h1 className="font-serif text-[26px] leading-tight text-slate-50 mt-0.5">{matter.title}</h1>
          {client && (
            <Link to={`/liq/clients/${client.id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-emerald-300 hover:text-emerald-200 hover:underline"
              data-testid="m-client-link">
              <Building2 size={13} /> {client.clientName}
            </Link>
          )}
        </div>
        <Badge tone="sky">{matter.stage}</Badge>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* LEFT — chat + context/docs */}
        <div className="space-y-5">
          {/* Description + Context (inline edit) + Discussion log */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="m-context">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">Matter brief</div>
              {dirty && (
                <button onClick={saveText} disabled={saving}
                  className="inline-flex items-center gap-1 text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1 font-semibold"
                  data-testid="m-save"><Save size={11} strokeWidth={2.5} />{saving ? 'Saving…' : 'Save'}</button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {/* LEFT: Description + Context */}
              <div className="space-y-3">
                <div>
                  <Label>Description</Label>
                  <textarea rows={2} value={descDraft} onChange={(e) => setDescDraft(e.target.value)}
                    placeholder="One-line summary" data-testid="m-desc"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none" />
                </div>
                <div>
                  <Label>Context — what does the client want?</Label>
                  <textarea rows={7} value={ctxDraft} onChange={(e) => setCtxDraft(e.target.value)}
                    placeholder="Client objective, constraints, urgency, expected outcome…" data-testid="m-ctx"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none" />
                </div>
              </div>
              {/* RIGHT: Discussion log */}
              <DiscussionLog matterId={id} entries={matter.discussionLog || []} currentUser={{ id: matter.createdBy, role: 'Legal IQ Lead' }} onChanged={load} />
            </div>
          </div>

          {/* Generated documents by agents */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="m-generated-docs">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">Agent-drafted documents · {generatedDocs.length}</div>
                <div className="text-slate-500 text-[11px] mt-0.5">Preparer drafts using full company + matter context. Approve, request changes, or reject — every decision posts to the group chat.</div>
              </div>
              <button onClick={() => setGenOpen(true)}
                className="inline-flex items-center gap-1.5 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[12px] px-4 py-2 rounded-full hover:brightness-110 hover:scale-[1.02] shadow-lg"
                data-testid="m-generate-doc">
                <FilePlus size={13} strokeWidth={2.5} /> Ask Preparer to draft…
              </button>
            </div>
            {/* Filter chips */}
            {generatedDocs.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {['All', 'Draft', 'Changes Required', 'Approved', 'Rejected'].map((s) => (
                  <button key={s} onClick={() => setDocsFilter(s)}
                    className={`text-[10.5px] px-2.5 py-1 rounded-full border transition-all ${
                      docsFilter === s
                        ? 'bg-amber-500/15 border-amber-500/40 text-amber-100'
                        : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid={`docs-filter-${s.replace(/\s+/g, '-')}`}>
                    {s} <span className="opacity-60">{statusCounts[s]}</span>
                  </button>
                ))}
              </div>
            )}
            {filteredGen.length === 0 ? (
              <div className="text-slate-600 text-[12px] italic py-6 text-center">
                {generatedDocs.length === 0
                  ? 'No drafts yet. Click "Ask Preparer to draft…" to generate the first document.'
                  : `No documents in "${docsFilter}".`}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredGen.map((d) => (
                  <GeneratedDocCard key={d.id} matterId={id} doc={d} onReview={reviewDoc} busy={reviewBusy} />
                ))}
              </div>
            )}
          </div>

          {/* Group Chat */}
          <GroupChat key={chatBumpKey} matterId={id} matter={matter} agentsById={agents} />
        </div>

        {/* RIGHT — 3 agents, docs, company */}
        <div className="space-y-4">
          {/* 3 Assigned agents */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="m-team">
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70 mb-3">Assigned agents</div>
            <div className="space-y-2.5">
              {teamAgents.map(({ role, id: aid }) => {
                const a = agents[aid];
                if (!a) return null;
                return (
                  <div key={role} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                    <AgentAvatar agent={a} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-amber-200/60">{role}</div>
                      <div className="text-slate-100 text-[13px] truncate">{a.fullName}</div>
                      <div className="text-slate-500 text-[10px] truncate">{a.designation}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reference (uploaded) documents */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="m-docs">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">
                Reference uploads · {uploadedDocs.length}
              </div>
            </div>
            <DocUploader matterId={id} onUploaded={load} />
            {uploadedDocs.length === 0 ? (
              <div className="text-slate-600 text-[12px] italic py-3">No uploads. Attach reference PDFs / drafts / KYC.</div>
            ) : (
              <div className="space-y-1.5 mt-3">
                {uploadedDocs.map((d) => <DocRow key={d.id} doc={d} onOpen={openDoc} onDelete={deleteDoc} />)}
              </div>
            )}
          </div>

          {/* Client card mini */}
          {client && (
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="m-client-card">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70 mb-3">Company</div>
              <Link to={`/liq/clients/${client.id}`} className="group block">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Building2 size={16} className="text-emerald-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-100 text-[13px] font-medium truncate group-hover:text-amber-100">{client.clientName}</div>
                    <div className="text-slate-500 text-[10px]">{client.type} · {(client.documents || []).length} docs</div>
                  </div>
                </div>
                {client.context && <div className="text-slate-400 text-[11.5px] line-clamp-3">{client.context}</div>}
              </Link>
            </div>
          )}
        </div>
      </div>

      <GenerateDocModal open={genOpen} matterId={id} onClose={() => setGenOpen(false)} onDone={() => load()} />
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{children}</div>;
}
